import { useEffect, useState } from "react";
import { toast } from "sonner";
import { listarActividades, registrarGPS, subirFoto, compressFile } from "./api";

const hoy = () => new Date().toISOString().slice(0, 10);
const SLOTS = [
  { key: "inicio",  label: "INICIO"  },
  { key: "proceso", label: "PROCESO" },
  { key: "final",   label: "FINAL"   },
];

function FotoBox({ slot, src, onPick, subiendo, disabled }) {
  const id = `foto-${slot.key}`;
  return (
    <label htmlFor={id} className={`via-foto-box ${src ? "filled" : ""}`}
           data-testid={`lider-foto-${slot.key}`}
           style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: subiendo ? 0.6 : 1 }}>
      {src ? (
        <>
          <img src={src} alt={slot.label}/>
          <span className="check"><i className="fa-solid fa-check"/></span>
        </>
      ) : (
        <div style={{ textAlign: "center", color: "#94A3B8" }}>
          <i className={`fa-solid ${subiendo ? "fa-spinner fa-spin" : "fa-camera"}`} style={{ fontSize: 22 }}/>
          <div style={{ fontSize: 11, fontWeight: 700, marginTop: 4 }}>{slot.label}</div>
        </div>
      )}
      <input id={id} type="file" accept="image/*" style={{ display: "none" }}
             disabled={disabled || subiendo}
             onChange={(e) => onPick(e.target.files?.[0])} />
    </label>
  );
}

function badgeTipo(tipo) {
  if (["ALCALDE","REGIDOR","DIRECTOR DMU","SUBDIRECTOR ESPACIOS PÚBLICOS","DESPACHO","CONSERJE","AVANZADA ALCALDE"].includes(tipo))
    return "via-badge via-badge-poli";
  if (tipo === "CRC" || tipo === "OFICIO") return "via-badge via-badge-crc";
  return "via-badge via-badge-ord";
}

export default function VistaLider({ user }) {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [gpsCargando, setGpsCargando] = useState(null);
  const [fotoCargando, setFotoCargando] = useState(null); // `${id}-${slot}`

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await listarActividades({ fecha_desde: hoy(), fecha_hasta: hoy() });
      setItems(r.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando mis órdenes");
    } finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, []);

  const handleGPS = async (id) => {
    if (!navigator.geolocation) { toast.error("Tu navegador no soporta GPS"); return; }
    setGpsCargando(id);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await registrarGPS(id, pos.coords.latitude, pos.coords.longitude);
          toast.success("Ubicación registrada");
          cargar();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error al registrar GPS");
        } finally { setGpsCargando(null); }
      },
      (err) => { toast.error("No se pudo obtener GPS: " + err.message); setGpsCargando(null); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleFoto = async (id, slot, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("El archivo no es una imagen"); return; }
    const key = `${id}-${slot}`;
    setFotoCargando(key);
    try {
      const dataURL = await compressFile(file);
      await subirFoto(id, slot, dataURL);
      toast.success(`Foto ${slot.toUpperCase()} subida`);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir foto");
    } finally { setFotoCargando(null); }
  };

  if (cargando) return <div className="via-empty">Cargando…</div>;
  if (!items.length) return (
    <div className="via-card via-empty" data-testid="lider-empty">
      <i className="fa-solid fa-clipboard-list" style={{ fontSize: 32, color: "#CBD5E1" }}/>
      <div style={{ marginTop: 8 }}>No tienes órdenes asignadas hoy.</div>
    </div>
  );

  return (
    <div data-testid="lider-lista">
      {items.map((it) => {
        const coordLat = it.gps_lat ?? it.coordenadas_alexa_lat;
        const coordLng = it.gps_lng ?? it.coordenadas_alexa_lng;
        const tieneCoordMesa = it.coordenadas_alexa_lat != null;
        return (
          <div key={it.id} className="via-card" data-testid={`lider-orden-${it.id}`}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
              <span className={badgeTipo(it.tipo_atencion)}>{it.tipo_atencion}</span>
              <span className="via-badge via-badge-ord">{it.area_intervencion}</span>
              {it.turno && <span className="via-badge via-badge-ord">{it.turno}</span>}
              {it.numero_unidad && <span style={{ fontSize: 12, color: "#64748B" }}>Unidad {it.numero_unidad}</span>}
            </div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{it.intervencion_en || it.ubicacion || "(sin lugar)"}</div>
            {it.direccion_referencia && <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>{it.direccion_referencia}</div>}
            {(it.actividades || []).length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                <strong>Actividades:</strong> {it.actividades.join(" · ")}
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 12, color: "#475569" }}>
              {it.bolsas_negras > 0 && <>🗑️ {it.bolsas_negras} bolsas · {it.basura_kg} kg · {it.basura_toneladas} ton<br/></>}
              {it.metros_lineales != null && <>📏 {it.metros_lineales} m ({it.tipo_frente})<br/></>}
              {it.metros_cuadrados ? <>📐 {it.metros_cuadrados} m²<br/></> : null}
            </div>
            {coordLat != null && coordLng != null && (
              <a className="via-mapa-pin" href={`https://www.google.com/maps?q=${coordLat},${coordLng}`}
                 target="_blank" rel="noreferrer" data-testid={`lider-ir-mapa-${it.id}`}>
                <i className="fa-solid fa-map-pin"/>{tieneCoordMesa ? "Ir al lugar" : "Ver mi GPS"}
              </a>
            )}
            <div className="via-foto-grid" style={{ marginTop: 10 }}>
              {SLOTS.map((s) => (
                <FotoBox key={s.key} slot={s}
                         src={it[`foto_${s.key}`]}
                         subiendo={fotoCargando === `${it.id}-${s.key}`}
                         onPick={(f) => handleFoto(it.id, s.key, f)}/>
              ))}
            </div>
            {(it.tipo_atencion === "ORDINARIO" || !tieneCoordMesa) && (
              <button className="via-btn-amber" style={{ marginTop: 12, width: "100%" }}
                      data-testid={`lider-btn-gps-${it.id}`}
                      disabled={gpsCargando === it.id}
                      onClick={() => handleGPS(it.id)}>
                <i className="fa-solid fa-location-crosshairs"/>{" "}
                {gpsCargando === it.id ? "Capturando…" : (it.gps_lat ? "Actualizar mi ubicación" : "Registrar mi ubicación")}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
